import { config } from '../config';
import { sanitiseDesign, type Design } from '../design/schema';

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${config.apiBase}${path}`, {
      ...init,
      headers: { ...(init?.body ? { 'Content-Type': 'application/json' } : {}), ...init?.headers },
    });
  } catch {
    throw new ApiError('Could not reach the server. Check your connection and try again.', 0);
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(body?.error ?? `Request failed (${res.status})`, res.status);
  }
  return res.json();
}

export type DesignStatus = 'draft' | 'ordered';
export interface SavedDesign {
  id: string;
  status: DesignStatus;
  updatedAt: string;
  design: Design;
}

const parse = (r: any): SavedDesign => ({ id: r.id, status: r.status, updatedAt: r.updatedAt, design: { ...sanitiseDesign(r.design), id: r.id } });

export const createDesign = (design: Design) =>
  request<any>('/designs', { method: 'POST', body: JSON.stringify({ design }) }).then(parse);

export const loadDesign = (id: string) => request<any>(`/designs/${encodeURIComponent(id)}`).then(parse);

export const updateDesign = (id: string, design: Design) =>
  request<any>(`/designs/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify({ design }) }).then(parse);

/** The permanent address of a saved design's thumbnail (rendered on first request if needed). */
export const previewUrl = (id: string) => absoluteUrl(`${config.apiBase}/designs/${encodeURIComponent(id)}/preview.jpg`);

/** Render the saved design's preview image; returns its permanent URL (used as the cart thumbnail). */
export const renderSavedPreview = (id: string) =>
  request<{ url: string }>(`/designs/${encodeURIComponent(id)}/preview`, { method: 'POST' }).then((r) => absoluteUrl(r.url));

/** Store the customer-side capture of the poster with the saved design. */
export const uploadCustomerCapture = (id: string, image: string) =>
  request<{ ok: true }>(`/designs/${encodeURIComponent(id)}/customer`, { method: 'POST', body: JSON.stringify({ image }) });

/** Local development only: mark the design as ordered, as the Shopify order webhook does in production. */
export const simulateOrder = (id: string) =>
  request<{ adminUrl: string }>(`/designs/${encodeURIComponent(id)}/order`, { method: 'POST' });

/** Render any design (saved or not) through the print pipeline; returns an image blob. */
export async function renderPreview(design: Design, scale = 2): Promise<Blob> {
  let res: Response;
  try {
    res = await fetch(`${config.apiBase}/render/preview?scale=${scale}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ design }),
    });
  } catch {
    throw new ApiError('Could not reach the server.', 0);
  }
  if (!res.ok) throw new ApiError((await res.json().catch(() => null))?.error ?? 'Preview failed', res.status);
  return res.blob();
}

export interface ServerConfig {
  strava: { clientId: string } | null;
}
export const loadServerConfig = () => request<ServerConfig>('/designer/config').catch((): ServerConfig => ({ strava: null }));

export const exchangeStravaCode = (code: string) =>
  request<{ accessToken: string; athlete: { firstname?: string } | null }>('/strava/token', { method: 'POST', body: JSON.stringify({ code }) });

function absoluteUrl(url: string) {
  if (/^https?:/.test(url)) return url;
  const base = /^https?:/.test(config.apiBase) ? new URL(config.apiBase).origin : location.origin;
  return new URL(url, base).toString();
}
